/**
 * Adaptive interval multipliers (PRD §7, single source — tests assert from
 * these consts). Priority: digit/symbol, caps acronym, short high-frequency,
 * then core length.
 */
export const ADAPTIVE_MULTIPLIERS = {
    digitOrSymbol: 1.5,
    capsAcronym: 1.3,
    highFreqShort: 0.7,
    short: 1.0,
    medium: 1.2,
    long: 1.4,
};
/**
 * Core-length thresholds (grapheme clusters) for the adaptive classes.
 * The high-frequency length gate is per-language data
 * (LanguagePack.highFreqGateClusters), applied by the tokenizer when it
 * sets flags.isHighFreq — not a global const.
 */
export const ADAPTIVE_LENGTH = {
    shortMax: 7,
    mediumMax: 11,
};
/** Smart-pause multipliers (PRD §7, single source). */
export const PAUSE_MULTIPLIERS = {
    chapterEnd: 3.0,
    paragraphEnd: 2.5,
    sentencePunct: 2.0,
    clausePunct: 1.5,
    dashEllipsis: 1.5,
    none: 1.0,
};
const SENTENCE_PUNCT = new Set(['.', '!', '?']);
const CLAUSE_PUNCT = new Set([',', ';', ':']);
const DASH_PUNCT = new Set(['—', '…']);
export function adaptiveMultiplier(token) {
    if (token.flags.hasDigitOrSymbol)
        return ADAPTIVE_MULTIPLIERS.digitOrSymbol;
    if (token.flags.isCapsAcronym)
        return ADAPTIVE_MULTIPLIERS.capsAcronym;
    if (token.flags.isHighFreq)
        return ADAPTIVE_MULTIPLIERS.highFreqShort;
    if (token.coreLen <= ADAPTIVE_LENGTH.shortMax)
        return ADAPTIVE_MULTIPLIERS.short;
    if (token.coreLen <= ADAPTIVE_LENGTH.mediumMax)
        return ADAPTIVE_MULTIPLIERS.medium;
    return ADAPTIVE_MULTIPLIERS.long;
}
/** Closing-class trailers the pause scan skips: \p{Pe} like ) ] }, \p{Pf} like ” » ’, straight ' and ". */
const CLOSING_TRAILER = /^[\p{Pe}\p{Pf}'"]$/u;
export function pauseMultiplier(token) {
    if (token.flags.endsChapter)
        return PAUSE_MULTIPLIERS.chapterEnd;
    if (token.flags.endsParagraph)
        return PAUSE_MULTIPLIERS.paragraphEnd;
    return trailingPauseMultiplier(token);
}
/**
 * F2: the pause-relevant mark may be wrapped in closing punctuation
 * (End.” / End.) / word,”). Scan backward over closing-class clusters to the
 * first pause-relevant mark and use its multiplier. A run of ≥2 '.' reads as
 * an ellipsis (dash/ellipsis class); a single '.' stays sentence-final.
 */
function trailingPauseMultiplier(token) {
    let i = clusterCountOf(token) - 1;
    while (i >= 0 && CLOSING_TRAILER.test(clusterAt(token, i)))
        i -= 1;
    if (i < 0)
        return PAUSE_MULTIPLIERS.none;
    const mark = clusterAt(token, i);
    if (mark === '.') {
        return i >= 1 && clusterAt(token, i - 1) === '.'
            ? PAUSE_MULTIPLIERS.dashEllipsis
            : PAUSE_MULTIPLIERS.sentencePunct;
    }
    if (SENTENCE_PUNCT.has(mark))
        return PAUSE_MULTIPLIERS.sentencePunct;
    if (CLAUSE_PUNCT.has(mark))
        return PAUSE_MULTIPLIERS.clausePunct;
    if (DASH_PUNCT.has(mark))
        return PAUSE_MULTIPLIERS.dashEllipsis;
    return PAUSE_MULTIPLIERS.none;
}
/**
 * Display interval for one token at the given effective WPM.
 * Chunk entries sum member intervals — never interval×chunkSize (PD-6).
 */
export function tokenIntervalMs(token, effectiveWpm, adaptiveEnabled = true) {
    const base = 60000 / effectiveWpm;
    const adaptive = adaptiveEnabled ? adaptiveMultiplier(token) : 1.0;
    return base * adaptive * pauseMultiplier(token);
}
function clusterCountOf(token) {
    return token.clusterToUnitOffsets.length - 1;
}
function clusterAt(token, i) {
    const start = token.clusterToUnitOffsets[i];
    const end = token.clusterToUnitOffsets[i + 1];
    if (start === undefined || end === undefined)
        return '';
    return token.raw.slice(start, end);
}
//# sourceMappingURL=timing.js.map